import { z } from "zod";
import { Step1Schema } from "./step1";
import { Step2Schema } from "./step2";
import { Step3Schema } from "./step3";
import { Step4Schema } from "./step4";
import { Step5Schema } from "./step5";
import { Step6Schema } from "./step6";
import { Step7Schema } from "./step7";
import { Step8Schema } from "./step8";
import { Step9Schema } from "./step9";
import { Step10Schema, getRequiredDocumentTypes } from "./step10";

const AUTO_HAULER_COMMODITIES = new Set([
  "automobiles_5_vehicles_or_less",
  "automobiles_6_vehicles_or_more",
]);

export * from "./step1";
export * from "./step2";
export * from "./step3";
export * from "./step4";
export * from "./step5";
export * from "./step6";
export * from "./step7";
export * from "./step8";
export * from "./step9";
export * from "./step10";
export * from "./parseCurrencyToNumber";
export * from "./currentYear";

export const FullSchemaBase = z.object({
  step1: Step1Schema,
  step2: Step2Schema,
  step3: Step3Schema,
  step4: Step4Schema,
  step5: Step5Schema,
  step6: Step6Schema,
  step7: Step7Schema,
  step8: Step8Schema,
  step9: Step9Schema,
  step10: Step10Schema,
});

/** Plug into your FullSchema */
export const FullSchema = FullSchemaBase.superRefine((data, ctx) => {
  const carriers = Array.isArray(data?.step1?.carrier)
    ? data.step1.carrier
    : data?.step1?.carrier
    ? [data.step1.carrier]
    : [];
  const hasCoverWhale = carriers.some(
    (c) => String(c).toLowerCase() === "cover whale"
  );
  const garageState = String(
    data?.step2?.garagingAddress?.state || data?.step1?.garageState || ""
  ).trim().toLowerCase();
  const isNjRisk = garageState === "new jersey" || garageState === "nj";
  const coverWhaleAlLimit =
    data?.step8?.auto_liability_limit_for_cover_whale_and_nirvana;

  if (
    data?.step8?.auto_liability &&
    hasCoverWhale &&
    isNjRisk &&
    coverWhaleAlLimit !== "1500000"
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["step8", "auto_liability_limit_for_cover_whale_and_nirvana"],
      message: "New Jersey requires a $ 1,500,000 Auto Liability Limit for Cover Whale",
    });
  }

  if (
    data?.step8?.auto_liability &&
    hasCoverWhale &&
    !isNjRisk &&
    coverWhaleAlLimit === "1500000"
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["step8", "auto_liability_limit_for_cover_whale_and_nirvana"],
      message: "$ 1,500,000 Auto Liability Limit is only available for New Jersey",
    });
  }

  const docs = data?.step10?.documents ?? [];
  const missing = getRequiredDocumentTypes(data).filter(
    (t) =>
      !(docs || []).some(
        (d) => d.type === t && (d.url || "").trim().length
      )
  );

  if (missing.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["step10", "documents"],
      message: `Documents required: ${missing
        .map((m) => m.toUpperCase())
        .join(", ")}`,
    });
  }

  const vehicleCount = data?.step6?.vehicles?.length ?? 0;
  const targetPrice = data?.step10?.targetPricePerTruck;
  if (vehicleCount >= 6 && !String(targetPrice || "").trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["step10", "targetPricePerTruck"],
      message: "Target price per truck is required for 6+ vehicles",
    });
  }

  const hasAutoHaulerCommodity = (data?.step8?.commodity_array ?? []).some(
    (commodity) => AUTO_HAULER_COMMODITIES.has(commodity?.commodityKey)
  );

  if (
    hasCoverWhale &&
    hasAutoHaulerCommodity &&
    !["Y", "N"].includes(data?.step3?.opsTowingRep)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["step3", "opsTowingRep"],
      message:
        "Please answer whether the insured's operation includes towing, repossession or driveaway",
    });
  }
});
